import type { Editor } from 'tinymce'
import type { ThemeControlledProperty } from './detectStyleChange'

import {
  getCSSPropertyFromCommand,
  getCSSPropertyFromFormat,
  isThemeControlledProperty,
} from './detectStyleChange'
import { addPreservedStyle } from './markUserStyles'

/**
 * The selector for service information template containers.
 * Style preservation only applies to elements within these containers.
 */
const SERVICE_TEMPLATE_SELECTOR = '#service-information-template'

/**
 * Checks if an element is within a service information template.
 *
 * @param element - The DOM element to check
 * @returns true if the element is inside a service template
 */
const isWithinServiceTemplate = (element: Element | null): boolean => {
  if (!element) return false
  return element.closest(SERVICE_TEMPLATE_SELECTOR) !== null
}

/**
 * Marks style preservation for the current selection or node when a style command is executed.
 * Only marks elements that are within a service information template.
 *
 * @param editor - The TinyMCE editor instance
 * @param cssProperty - The CSS property being modified
 */
const markStylePreservationForSelection = (
  editor: Editor,
  cssProperty: ThemeControlledProperty,
): void => {
  const selectedNode = editor.selection?.getNode()
  if (!selectedNode || !isWithinServiceTemplate(selectedNode)) return

  // Get all text-bearing elements in the selection
  const range = editor.selection?.getRng()
  if (!range) return

  // If we have a collapsed selection, mark the immediate container
  if (range.collapsed) {
    const container = range.startContainer
    const element =
      container.nodeType === Node.ELEMENT_NODE
        ? (container as HTMLElement)
        : container.parentElement

    if (element && isWithinServiceTemplate(element)) {
      markAffectedElement(element, cssProperty)
    }
    return
  }

  // For non-collapsed selections, mark only the elements that will receive the style
  // TinyMCE typically wraps styled content in spans or applies to existing elements
  if (
    selectedNode instanceof HTMLElement &&
    isWithinServiceTemplate(selectedNode)
  ) {
    // Mark the selected node itself if it's a text container
    markAffectedElement(selectedNode, cssProperty)

    // Mark any styled spans/elements within the selection that have the property
    // These are the elements that TinyMCE creates/modifies for the style
    const styledElements = selectedNode.querySelectorAll('[style]')
    styledElements.forEach((el) => {
      if (el instanceof HTMLElement && isWithinServiceTemplate(el)) {
        // Only mark if this element actually has the style property set
        const computedStyle = el.style.getPropertyValue(cssProperty)
        if (computedStyle) {
          addPreservedStyle(el, cssProperty)
        }
      }
    })
  }
}

/**
 * Marks the element that received the style change with a preserved style.
 * We only mark the directly affected element, not parents or children,
 * to allow fine-grained control over which elements keep user customizations.
 *
 * Note: We intentionally don't propagate to parent/child elements because:
 * - Parent elements: Would prevent theme from applying to siblings
 * - Child elements: Child should get theme styles unless user explicitly modifies them
 *
 * @param element - The element that received the style change
 * @param cssProperty - The CSS property to preserve
 */
const markAffectedElement = (
  element: HTMLElement,
  cssProperty: ThemeControlledProperty,
): void => {
  if (!isWithinServiceTemplate(element)) return

  // Only mark elements that can have text styling
  const isTextContainer = [
    'P',
    'SPAN',
    'DIV',
    'TD',
    'TH',
    'LI',
    'STRONG',
    'EM',
    'B',
    'I',
  ].includes(element.tagName)

  if (isTextContainer) {
    addPreservedStyle(element, cssProperty)
  }
}

/**
 * Handles direct style attribute changes on elements within service templates.
 * This detects when inline styles are modified programmatically.
 *
 * @param element - The element whose style was changed
 * @param styleProperty - The specific CSS property that was changed (optional)
 */
export const handleDirectStyleChange = (
  element: HTMLElement,
  styleProperty?: string,
): void => {
  if (!isWithinServiceTemplate(element)) return

  if (styleProperty && isThemeControlledProperty(styleProperty)) {
    addPreservedStyle(element, styleProperty)
  }
}

/**
 * Sets up style preservation handlers for a TinyMCE editor instance.
 * This should be called during TinyMCE setup/initialization.
 *
 * @param editor - The TinyMCE editor instance
 */
export const setupServiceTemplateStylePreservation = (editor: Editor): void => {
  // Handle commands like fontname, forecolor, bold, italic
  editor.on('ExecCommand', (e) => {
    if (!e?.command) return

    const cssProperty = getCSSPropertyFromCommand(e.command)
    if (cssProperty) {
      markStylePreservationForSelection(editor, cssProperty)
    }
  })

  // Handle format applications (bold, italic, etc.)
  editor.on('FormatApply', (e) => {
    if (!e?.format) return

    const cssProperty = getCSSPropertyFromFormat(e.format)
    if (cssProperty) {
      markStylePreservationForSelection(editor, cssProperty)
    }
  })

  // Handle format removals - we still want to preserve the user's choice
  // to remove formatting (e.g., removing bold is a user customization)
  editor.on('FormatRemove', (e) => {
    if (!e?.format) return

    const cssProperty = getCSSPropertyFromFormat(e.format)
    if (cssProperty) {
      markStylePreservationForSelection(editor, cssProperty)
    }
  })
}

export default setupServiceTemplateStylePreservation
